import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import Joyride, { EVENTS } from "react-joyride";
import { X } from "lucide-react";
import { updateOnboarding } from "@/api/users.api";
import { setOnboardingTourState } from "@/hooks/useOnboardingUiEnabled";
import { isDemoUser } from "@/utils/userFlags";

const STORAGE_VERSION = "v1";

function safeParseJson(raw, fallback) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function getUserKey(user) {
  return user?._id || user?.email || "unknown";
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function normalizePath(p) {
  if (!p) return "";
  const out = p.replace(/\/+$/, "");
  return out === "" ? "/" : out;
}

function samePath(a, b) {
  return normalizePath(a) === normalizePath(b);
}

function clickFirstMatchingSelector(selector) {
  if (!selector) return false;
  const el = document.querySelector(selector);
  if (!el) return false;
  try {
    el.click?.();
    return true;
  } catch {
    return false;
  }
}

function sleepMs(ms) {
  if (!ms || ms <= 0) return Promise.resolve();
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function getRectForSelector(selector) {
  if (!selector) return null;
  const els = document.querySelectorAll(selector);
  for (const el of els) {
    if (!(el instanceof Element)) continue;
    const rect = el.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) continue;
    return rect;
  }
  return null;
}

function isSelectorVisible(selector) {
  return !!getRectForSelector(selector);
}

function useTargetRect(selector, enabled) {
  const [rect, setRect] = useState(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const update = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setRect(getRectForSelector(selector));
      });
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);

    const interval = window.setInterval(update, 500);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      window.clearInterval(interval);
    };
  }, [selector, enabled]);

  return rect;
}

function readLocalMap(key) {
  try {
    return safeParseJson(localStorage.getItem(key), {});
  } catch {
    return {};
  }
}

function writeLocalMap(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function Beacon({ beaconId, selector, userKey, storageKey, enabled, onOpen }) {
  const [dismissed, setDismissed] = useState(false);
  const rect = useTargetRect(selector, enabled);

  useEffect(() => {
    if (!enabled) return;
    const map = readLocalMap(storageKey);
    setDismissed(map?.[beaconId] === true);
  }, [enabled, storageKey, beaconId]);

  if (!enabled || dismissed || !rect) return null;

  const dotSize = 24;
  const offset = 8;
  let left = rect.left + rect.width / 2;
  let top = rect.top + rect.height / 2;

  left = clamp(left, 8, window.innerWidth - dotSize - 8);
  top = clamp(top, 8, window.innerHeight - dotSize - 8);

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] pointer-events-none"
      aria-hidden={false}
    >
      <button
        type="button"
        className="fixed -translate-x-1/2 -translate-y-1/2 pointer-events-auto w-7 h-7 rounded-full bg-[#4cc68d] shadow-lg focus:outline-none"
        style={{ left, top }}
        aria-label="Help tip"
        data-onboarding-dot={`${userKey}:${beaconId}`}
        onClick={() => onOpen?.(beaconId)}
      >
        <span className="absolute -inset-2 rounded-full border-1 border-[#4cc68d]/80 bg-[#4cc68d]/40 animate-ping" />
        <span className="absolute inset-1 rounded-full bg-[#4cc68d]" />
      </button>
    </div>,
    document.body,
  );
}

function JoyrideTooltip({ index, size, step, tooltipProps }) {
  const meta = step?.data || {};
  const handlersRef = meta?._handlersRef;
  const bodyImage = meta?.bodyImage || null;
  const isLastStep = index === size - 1;
  const isFirstStep = index === 0;
  const isNextDisabled =
    !!meta?.nextDisabledUntilSelector &&
    !isSelectorVisible(meta.nextDisabledUntilSelector);

  const tooltipWidthClass =
    "w-[min(430px,calc(100vw-40px))] sm:w-[min(430px,calc(100vw-24px))]";

  const tourTitle = isFirstStep
    ? "Take a Tour of the App"
    : isLastStep
      ? "Congratulations on Finishing the Tour!"
      : "App Tour";

  return (
    <div
      {...tooltipProps}
      className={
        (tooltipProps?.className ? `${tooltipProps.className} ` : "") +
        `${tooltipWidthClass} rounded-lg border-2 border-[#4cc68d]/70 bg-white p-4 shadow-2xl`
      }
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-4">
            <div className="text-sm font-semibold text-[#2f8f63] uppercase">
              {tourTitle}
            </div>
            <div className="text-xs text-gray-500 flex-shrink-0">
              {index + 1} / {size}
            </div>
          </div>
        </div>
        {!isLastStep && (
          <button
            type="button"
            className="group shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg text-gray-700 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#4cc68d]/60"
            aria-label="Close"
            onClick={() => handlersRef?.current?.close?.()}
          >
            <X className="w-5 h-5 transition-colors" />
          </button>
        )}
      </div>

      {(() => {
        const bodyContent = meta?.body || step?.content;
        if (typeof bodyContent !== "string" || !bodyImage?.src) {
          return (
            <div className="mt-1 text-sm leading-snug text-gray-800 whitespace-pre-wrap">
              {bodyContent}
            </div>
          );
        }

        return (
          <div className="mt-1 text-sm leading-snug text-gray-800">
            <div className="mt-1 text-sm leading-snug text-gray-800 whitespace-pre-wrap">
              {bodyContent}
            </div>
            <img
              src={bodyImage.src}
              alt={bodyImage.alt || "Illustration"}
              className="mt-3 w-full max-h-32 object-contain rounded-lg border border-black/10 bg-white"
              loading="lazy"
              draggable={false}
            />
          </div>
        );
      })()}

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {!isLastStep && (
            <button
              type="button"
              className="px-3 py-2 text-sm rounded-lg border border-black/10 bg-gray-100 text-gray-900 hover:bg-gray-200"
              onClick={() => handlersRef?.current?.skip?.()}
            >
              Skip Tour
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isFirstStep && (
            <button
              type="button"
              className="px-3 py-2 text-sm rounded-lg border border-black/10 bg-gray-100 text-gray-900 hover:bg-gray-200"
              onClick={() => handlersRef?.current?.back?.()}
            >
              Back
            </button>
          )}
          <button
            type="button"
            className={
              "px-3 py-2 text-sm rounded-lg bg-[#4cc68d] text-white hover:bg-[#3bb97f]" +
              (isNextDisabled
                ? " opacity-50 cursor-not-allowed hover:bg-[#4cc68d]"
                : "")
            }
            disabled={isNextDisabled}
            onClick={() => handlersRef?.current?.next?.()}
          >
            {isLastStep ? "Finish" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Tour({
  user,
  steps,
  requestedStepId,
  autoStart,
  onFinish,
  onComplete,
  onActiveChange,
}) {
  const userKey = getUserKey(user);
  const location = useLocation();
  const navigate = useNavigate();
  const storageKey = `onboarding_tour_${STORAGE_VERSION}:${userKey}`;

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 640;
  });
  const lastAutoAdvanceKeyRef = useRef(null);
  const lastNavDirectionRef = useRef("next"); // "next" | "back"
  const lastTargetNotFoundKeyRef = useRef(null);
  const pendingStepIndexRef = useRef(null);
  const handlersRef = useRef(null);

  const isAdult = user?.role !== "student";

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => setIsMobile(window.innerWidth <= 640);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isAdult || !autoStart || !userKey || userKey === "unknown") return;
    const state = safeParseJson(
      (() => {
        try {
          return localStorage.getItem(storageKey);
        } catch {
          return null;
        }
      })(),
      {},
    );
    if (state?.completed) return;
    setRun(true);
  }, [isAdult, autoStart, storageKey, userKey]);

  useEffect(() => {
    if (!isAdult) return;
    if (!requestedStepId) return;
    const idx = steps.findIndex((s) => s.id === requestedStepId);
    const nextIdx = idx >= 0 ? idx : 0;
    const requestedStep = steps[nextIdx] || null;

    if (
      requestedStep?.ensurePath &&
      !samePath(location.pathname, requestedStep.ensurePath)
    ) {
      pendingStepIndexRef.current = nextIdx;
      setRun(false);
      navigate(requestedStep.ensurePath);
      return;
    }

    setStepIndex(nextIdx);
    setRun(true);
  }, [requestedStepId, isAdult, steps, location.pathname, navigate]);

  useEffect(() => {
    onActiveChange?.(run);
  }, [run, onActiveChange]);

  const step = steps[stepIndex] || null;

  // Expose tour state for other screens/components (no prop drilling).
  useEffect(() => {
    setOnboardingTourState(!!run, step?.id || null);
    return () => setOnboardingTourState(false, null);
  }, [run, step?.id]);

  useEffect(() => {
    lastTargetNotFoundKeyRef.current = null;
  }, [stepIndex, run]);

  useEffect(() => {
    if (!run) return;
    const prevBodyOverflowX = document.body.style.overflowX;
    const prevHtmlOverflowX = document.documentElement.style.overflowX;
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = prevBodyOverflowX;
      document.documentElement.style.overflowX = prevHtmlOverflowX;
    };
  }, [run]);

  useEffect(() => {
    const pendingIdx = pendingStepIndexRef.current;
    if (pendingIdx == null) return;
    const pendingStep = steps[pendingIdx] || null;
    if (!pendingStep) {
      pendingStepIndexRef.current = null;
      return;
    }

    if (
      pendingStep.ensurePath &&
      !samePath(location.pathname, pendingStep.ensurePath)
    ) {
      return;
    }

    pendingStepIndexRef.current = null;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    setStepIndex(pendingIdx);
    setRun(true);
  }, [location.pathname, steps]);

  useEffect(() => {
    if (!run || !step?.ensurePath) return;
    if (!samePath(location.pathname, step.ensurePath)) {
      navigate(step.ensurePath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, stepIndex]);

  useEffect(() => {
    if (!run || !step?.advanceOnClickSelector) return;

    const handler = (event) => {
      const el =
        event?.target instanceof Element
          ? event.target.closest(step.advanceOnClickSelector)
          : null;
      if (!el) return;

      const key = `click:${stepIndex}:${step.id}`;
      if (lastAutoAdvanceKeyRef.current === key) return;
      lastAutoAdvanceKeyRef.current = key;

      const delay = step.advanceOnClickDelayMs ?? 0;
      window.setTimeout(() => {
        setStepIndex((i) => Math.min(i + 1, steps.length - 1));
      }, delay);
    };

    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [
    run,
    step?.advanceOnClickSelector,
    step?.advanceOnClickDelayMs,
    step?.id,
    stepIndex,
    steps.length,
  ]);

  // Auto-advance when a selector becomes visible (e.g., modal appears)
  useEffect(() => {
    if (!run || !step?.autoAdvanceOnSelector) return;

    const key = `auto:${stepIndex}:${step.id}`;
    const tick = () => {
      if (!run) return;
      if (lastAutoAdvanceKeyRef.current === key) return;
      if (!isSelectorVisible(step.autoAdvanceOnSelector)) return;
      lastAutoAdvanceKeyRef.current = key;
      setStepIndex((i) => Math.min(i + 1, steps.length - 1));
    };

    tick();
    const interval = window.setInterval(tick, 250);
    return () => window.clearInterval(interval);
  }, [run, step?.autoAdvanceOnSelector, step?.id, stepIndex, steps.length]);

  const joyrideSteps = useMemo(() => {
    return steps.map((s) => {
      const stepTarget = isMobile && s.mobileSelector ? s.mobileSelector : s.selector;
      const base = {
        target: stepTarget,
        content: s.body,
        disableBeacon: true,
        // spotlightClicks: s.allowInteraction !== false,
        data: { ...s, _handlersRef: handlersRef },
      };

      if (s.placement) return { ...base, placement: s.placement };
      if (s.preferRight && !isMobile) {
        return { ...base, placement: "right" };
      }
      return base;
    });
  }, [steps, stepIndex, location.pathname, run, isMobile]);

  const finishCompleted = (reason) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ completed: true }));
    } catch {}
    // Delay unmount so Joyride/Popper can clean up safely.
    window.setTimeout(() => {
      setRun(false);
      setStepIndex(0);
      pendingStepIndexRef.current = null;
    }, 0);
    onComplete?.();
    onFinish?.({ reason: reason || "completed", stepId: step?.id });
  };

  const closeTour = () => {
    // Pause the tour without marking it completed/skipped.
    window.setTimeout(() => {
      setRun(false);
      pendingStepIndexRef.current = null;
    }, 0);
    onFinish?.({ reason: "closed", stepId: step?.id });
  };

  const doNext = async () => {
    const cur = steps[stepIndex] || null;
    if (!cur) return;
    lastNavDirectionRef.current = "next";

    if (
      cur?.nextDisabledUntilSelector &&
      !isSelectorVisible(cur.nextDisabledUntilSelector)
    ) {
      return;
    }

    if (cur?.beforeNextClickSelector) {
      const didClick = clickFirstMatchingSelector(cur.beforeNextClickSelector);
      if (didClick) {
        if (cur?.autoAdvanceOnSelector) {
          setRun(false);
        }
        await sleepMs(cur.beforeNextDelayMs ?? 150);
      }
    }

    if (stepIndex >= steps.length - 1) {
      finishCompleted("completed");
      return;
    }

    const nextIdx = Math.min(stepIndex + 1, steps.length - 1);
    const nextStep = steps[nextIdx] || null;
    if (
      nextStep?.ensurePath &&
      !samePath(location.pathname, nextStep.ensurePath)
    ) {
      pendingStepIndexRef.current = nextIdx;
      setRun(false);
      navigate(nextStep.ensurePath);
      return;
    }

    setStepIndex(nextIdx);
    setRun(true);
  };

  const doBack = async () => {
    const cur = steps[stepIndex] || null;
    if (!cur) return;
    lastNavDirectionRef.current = "back";

    if (
      cur.id === "settings-nav-tabs" &&
      !steps.some((s) => s.id === "resources-top")
    ) {
      const studentSupportsTopIdx = steps.findIndex(
        (s) => s.id === "student-supports-top",
      );
      if (studentSupportsTopIdx >= 0) {
        const targetStep = steps[studentSupportsTopIdx] || null;
        if (
          targetStep?.ensurePath &&
          !samePath(location.pathname, targetStep.ensurePath)
        ) {
          pendingStepIndexRef.current = studentSupportsTopIdx;
          setRun(false);
          navigate(targetStep.ensurePath);
          return;
        }
        setStepIndex(studentSupportsTopIdx);
        return;
      }
    }

    if (cur.id === "resources-top") {
      const studentSupportsTopIdx = steps.findIndex(
        (s) => s.id === "student-supports-top",
      );
      if (studentSupportsTopIdx >= 0) {
        const targetStep = steps[studentSupportsTopIdx] || null;
        if (
          targetStep?.ensurePath &&
          !samePath(location.pathname, targetStep.ensurePath)
        ) {
          pendingStepIndexRef.current = studentSupportsTopIdx;
          setRun(false);
          navigate(targetStep.ensurePath);
          return;
        }
        setStepIndex(studentSupportsTopIdx);
        return;
      }
    }

    if (cur?.beforeBackClickSelector) {
      const didClick = clickFirstMatchingSelector(cur.beforeBackClickSelector);
      if (didClick) {
        await sleepMs(cur.beforeBackDelayMs ?? 150);
      }
    }

    const prevIdx = Math.max(0, stepIndex - 1);
    const prev = steps[prevIdx] || null;

    if (
      cur?.ensurePath === "/dashboard" &&
      prev?.ensurePath === "/studentdashboard"
    ) {
      const achievementsIdx = steps.findIndex(
        (s) => s.id === "achievements-popup",
      );
      if (achievementsIdx >= 0) {
        const achievementsStep = steps[achievementsIdx];
        if (
          achievementsStep?.ensurePath &&
          !samePath(location.pathname, achievementsStep.ensurePath)
        ) {
          pendingStepIndexRef.current = achievementsIdx;
          setRun(false);
          navigate(achievementsStep.ensurePath);
          return;
        }
        setStepIndex(achievementsIdx);
        return;
      }
    }

    if (prev?.ensurePath && !samePath(location.pathname, prev.ensurePath)) {
      pendingStepIndexRef.current = prevIdx;
      setRun(false);
      navigate(prev.ensurePath);
      return;
    }

    setStepIndex(prevIdx);
  };

  const doSkip = () => finishCompleted("skipped");
  const doClose = () => closeTour();

  // Expose handlers to the tooltip component via step.data._handlersRef.
  handlersRef.current = {
    next: () => void doNext(),
    back: () => void doBack(),
    skip: doSkip,
    close: doClose,
  };

  const handleJoyrideCallback = (data) => {
    const { type, index } = data || {};

    if (type === EVENTS.TARGET_NOT_FOUND) {
      const curStep = steps[index] || null;
      if (!curStep) return;

      // If we are on the wrong route, navigate first and retry the same step.
      if (
        curStep.ensurePath &&
        !samePath(location.pathname, curStep.ensurePath)
      ) {
        pendingStepIndexRef.current = index;
        setRun(false);
        navigate(curStep.ensurePath);
        return;
      }

      const key = `tnf:${index}:${curStep.id}`;
      if (lastTargetNotFoundKeyRef.current === key) return;
      lastTargetNotFoundKeyRef.current = key;

      const waitMs = curStep?.waitForTargetMs ?? 900;
      const dir = lastNavDirectionRef.current === "back" ? -1 : 1;
      window.setTimeout(() => {
        if (curStep.selector && !isSelectorVisible(curStep.selector)) {
          setStepIndex((i) => clamp(i + dir, 0, steps.length - 1));
        } else {
          lastTargetNotFoundKeyRef.current = null;
        }
      }, waitMs);
      return;
    }
  };

  if (!isAdult || !run || !step) return null;

  return (
    <Joyride
      steps={joyrideSteps}
      run={run}
      stepIndex={stepIndex}
      continuous={false}
      showSkipButton={false}
      showProgress={false}
      scrollToFirstStep={true}
      disableOverlayClose={true}
      spotlightClicks={false}
      spotlightPadding={6}
      disableScrollParentFix={true}
      tooltipComponent={JoyrideTooltip}
      callback={handleJoyrideCallback}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: "#4cc68d",
          arrowColor: "#4cc68d",
        },
        overlay: {
          backgroundColor: "rgba(0,0,0,0.2)",
        },
        spotlight: {
          borderRadius: 8,
          border: "2px solid rgba(76, 198, 141, 0.95)",
        },
      }}
    />
  );
}

export default function OnboardingGuide({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [adultRequestedStepId, setAdultRequestedStepId] = useState(null);
  const [isAdultTourActive, setIsAdultTourActive] = useState(false);
  const [isAdultOnboardingEnabled, setIsAdultOnboardingEnabled] =
    useState(false);
  const [beaconCursor, setBeaconCursor] = useState(0);
  const [dismissedBeaconMap, setDismissedBeaconMap] = useState({});

  const userKey = getUserKey(user);
  const beaconsStorageKey = `onboarding_beacons_${STORAGE_VERSION}:${userKey}`;
  const isAdult = user?.role !== "student";
  const isDemo = isDemoUser(user);
  const isNewAdultNeedingTour =
    !!isAdult && user?.agreedToTerms === true && user?.needsOnboarding === true;

  const steps = useMemo(() => {
    const base = [
      {
        id: "sidebar-menu",
        selector: '[data-onboarding-id="sidebar-nav"]',
        title: "Navigate the app",
        body: "Welcome to 1-2-3 Wellness! As a talented educator, this guide will help show you the powerful features of the app. Please guide your students as they practice the first few times (students do not get this guide). Enjoy!\n\nUse this navigation bar to visit key tabs in the app.",
        preferRight: true,
        ensurePath: "/",
      },
      {
        id: "home-start",
        selector: '[data-onboarding-id="home-start"]',
        title: "Start a wellness practice",
        body: "Click the “start” button to begin the 1-2-3 Wellness daily “check-in”. The wellness practice takes less than 2 minutes and helps prime your students' brains for learning, connection and calm.\n\nWe recommend that you and your students practice at least once a day to set the tone for success!",
        bodyImage: {
          src: "/images/brain/brain3.jpg",
          alt: "Brain illustration",
        },
        ensurePath: "/",
        // preferRight: true,
      },
      {
        id: "practice-settings",
        selector: '[data-onboarding-id="sidebar-settings"]',
        mobileSelector: '[data-onboarding-mobile-id="sidebar-hamburger"]',
        title: "Adjust practice settings",
        body: "During a wellness practice, click “Settings” from the menu to change a few practice options like volume, voice, and timer. \n\nOutside of a practice, clicking on the same “Settings” link from the menu opens the full Settings page.",
        ensurePath: "/",
        preferRight: true,
      },
      {
        id: "achievements-popup",
        selector: '[data-onboarding-id="achievements-popup"]',
        title: "Accomplishments",
        body: "This popup highlights your achievements as you practice wellness. Click on the coin, streak and badge icons to celebrate your progress.",
        ensurePath: "/studentdashboard",
        beforeNextClickSelector: '[data-onboarding-id="achievements-close"]',
        beforeNextDelayMs: 200,
      },
      {
        id: "mystory-top",
        selector: '[data-onboarding-id="mystory-top"]',
        title: "My Story tab",
        body: "Welcome the to “My Story tab”. This tab will help you learn about YOUR own wellness! Please invite students to check out their own “My Story” page regularly to reflect and learn.",
        ensurePath: "/studentdashboard",
      },
      {
        id: "emotions-helping",
        selector: '[data-onboarding-id="emotions-helping"]',
        title: "Emotions and What’s Helping",
        body: "As you practice wellness, you can hover on an emoticon to show what helps you.\n\nWe recommend scheduling 5 minutes each week for students to reflect on their personal data and learn that “no matter what emotion I feel, there is something that I can do to help myself”. This builds emotion regulation, agency and more!",
        ensurePath: "/studentdashboard",
      },
      {
        id: "educator-who",
        selector: '[data-onboarding-id="educator-who"]',
        title: "Who + Time",
        body: "The “Educator Dashboard” tab can help you learn about YOUR STUDENT’s wellness and how to promote positive outcomes for academics, behavior and attendance!\n\nSearch for wellness data by student, class or school.\n\nUse the time dropdown to search by time-period (ie. ten minutes, one week or more).",
        ensurePath: "/dashboard",
      },
      {
        id: "educator-emotions-breakdown",
        selector: '[data-onboarding-id="educator-emotions-breakdown"]',
        title: "Emotions Breakdown",
        body: "Click on an emotion like 'sad' to show which students have felt that emotion in the time-period selected above. This feature can help you see which students are feeling a specific emotion like 'sad'.\n\nYou’ll learn how students are feeling with one click of a button!",
        ensurePath: "/dashboard",
      },
      {
        id: "educator-emotions-whats-helping",
        selector: '[data-onboarding-id="educator-emotions-whats-helping"]',
        title: "Emotions & What’s Helping",
        body: "As your students practice, the 1-2-3 Wellness app will present you with wellness practices that your students have indicated as most helpful.\n\nClick on an emotion like “tired” to discover strengths-based solutions you can use to support students when they’re tired.",
        ensurePath: "/dashboard",
      },
      {
        id: "educator-recommendations",
        selector: '[data-onboarding-id="educator-recommendations"]',
        title: "Data-based Recommendations",
        body: "Click on “Boost Academics”, “Boost Behavior” and “Celebrate” to access data-based recommendations.",
        ensurePath: "/dashboard",
        beforeNextDelayMs: 150,
      },
      {
        id: "student-supports-top",
        selector: '[data-onboarding-id="student-supports-top"]',
        title: "Student Supports: MTSS",
        body: "The “Student Supports” tab features MTSS and empowers you to learn which students may benefit from more support.\n\nWhen a student appears in yellow or red, this indicates that the student may benefit from tier 2 or 3 support.",
        ensurePath: "/studentsupports",
      },
      {
        id: "student-supports-student-card",
        selector: '[data-onboarding-id="student-supports-student-card"]',
        title: "Student card",
        body: "Click on a student to learn more about their wellness and needs.",
        ensurePath: "/studentsupports",
        // allowInteraction: true,
        beforeNextClickSelector:
          '[data-onboarding-id="student-supports-student-card"]',
        beforeNextDelayMs: 150,
        autoAdvanceOnSelector:
          '[data-onboarding-id="student-supports-selected-student"]',
      },
      {
        id: "student-supports-modal-x",
        selector: '[data-onboarding-id="student-supports-modal-x"]',
        title: "Return to Student Supports",
        body: "Scroll down to learn more about this student's wellness, challenges and solutions to support their academic and behavioral success.\n\nClick the X or 'Back' button to go back to the main Student Supports screen.",
        ensurePath: "/studentsupports",
        // allowInteraction: true,
        advanceOnClickSelector:
          '[data-onboarding-id="student-supports-modal-x"]',
        // advanceOnClickDelayMs: 150,
      },
      {
        id: "resources-top",
        selector: '[data-onboarding-id="resources-top"]',
        title: "Resources",
        body: "The “Resources” tab offers powerful tools beyond the app such as adult wellness videos, an educator guide, lesson plans and more. Check them out below!",
        ensurePath: "/resourcestab",
      },
      {
        id: "settings-nav-tabs",
        selector: '[data-onboarding-id="settings-nav-tabs"]',
        title: "Settings",
        body: "This 'Settings' tab allows you to customize your experience.\n\nYou can always turn this tour back on using the “Take Tour” button at the bottom of the Settings page.\n\nNice job! You’re ready to begin using the app. Enjoy building wellness!",
        ensurePath: "/optionsscreen",
        preferRight: true,
        // allowInteraction: true,
      },
    ];

    if (isDemo) {
      return base.filter((s) => s.id !== "resources-top");
    }

    return base;
  }, [isDemo]);

  const beaconDots = useMemo(() => {
    return steps.map((s) => {
      if (s.id === "home-welcome" || s.id === "home-start") {
        return { ...s, enabled: location.pathname === "/" };
      }
      if (
        [
          "educator-dashboard-top",
          "educator-who",
          "educator-emotions-breakdown",
          "educator-emotions-whats-helping",
          "educator-recommendations",
        ].includes(s.id)
      ) {
        return { ...s, enabled: location.pathname === "/dashboard" };
      }
      if (
        [
          "student-supports-top",
          "student-supports-student-names",
          "student-supports-tier-settings",
          "student-supports-student-card",
          "student-supports-selected-student",
          "student-supports-alerts",
          "student-supports-modal-x",
          "student-supports-filters",
        ].includes(s.id)
      ) {
        return { ...s, enabled: location.pathname === "/studentsupports" };
      }
      if (s.id === "resources-top") {
        return { ...s, enabled: location.pathname === "/resourcestab" };
      }
      if (
        [
          "achievements-popup",
          "achievements-close",
          "mystory-top",
          "emotions-breakdown",
          "emotions-helping",
          "emotions-idea",
        ].includes(s.id)
      ) {
        return { ...s, enabled: location.pathname === "/studentdashboard" };
      }
      if (s.id === "settings-nav-tabs" || s.id === "settings-retake-tour") {
        return { ...s, enabled: location.pathname === "/optionsscreen" };
      }
      return { ...s, enabled: true };
    });
  }, [steps, location.pathname]);

  const canRender = !!user && userKey !== "unknown";
  const onboardingUiEnabled =
    isNewAdultNeedingTour ||
    isAdultOnboardingEnabled ||
    !!adultRequestedStepId ||
    isAdultTourActive;

  useEffect(() => {
    setBeaconCursor(0);
    setDismissedBeaconMap(readLocalMap(beaconsStorageKey));
  }, [beaconsStorageKey]);

  useEffect(() => {
    // If this adult user is flagged as needing onboarding, enable the onboarding UI
    // so beacons continue to work even if the tour is paused/closed.
    if (isNewAdultNeedingTour) {
      setIsAdultOnboardingEnabled(true);
    }
  }, [isNewAdultNeedingTour]);

  useEffect(() => {
    if (!canRender) return;
    if (isAdultTourActive) {
      setAdultRequestedStepId(null);
    }
  }, [isAdultTourActive, canRender]);

  const visibleBeacon = useMemo(() => {
    if (!canRender || !onboardingUiEnabled) return null;
    for (let i = beaconCursor; i < beaconDots.length; i += 1) {
      const candidate = beaconDots[i];
      if (!candidate?.enabled) continue;
      if (dismissedBeaconMap?.[candidate.id] === true) continue;
      return candidate;
    }
    return null;
  }, [
    beaconDots,
    beaconCursor,
    dismissedBeaconMap,
    canRender,
    onboardingUiEnabled,
  ]);

  useEffect(() => {
    if (!canRender || !isAdult) return;

    const handler = (event) => {
      const stepId = event?.detail?.stepId || "sidebar-menu";
      setIsAdultOnboardingEnabled(true);
      setBeaconCursor(0);
      setDismissedBeaconMap({});
      try {
        writeLocalMap(beaconsStorageKey, {});
      } catch {}
      setAdultRequestedStepId(stepId);
    };

    window.addEventListener("onboarding:retake", handler);
    return () => window.removeEventListener("onboarding:retake", handler);
  }, [beaconsStorageKey, canRender, isAdult]);

  // No onboarding at all for students.
  if (!canRender || !isAdult) return null;

  const persistTourCompletion = async () => {
    if (!user?._id) return;

    try {
      await updateOnboarding(false);
    } catch {
      // Best-effort only. local state still closes the tour for this session.
    }
  };

  const dismissAllBeacons = () => {
    const next = { ...readLocalMap(beaconsStorageKey) };
    for (const s of steps) next[s.id] = true;
    writeLocalMap(beaconsStorageKey, next);
    setDismissedBeaconMap(next);
  };

  return (
    <>
      {isAdult && (
        <Tour
          user={user}
          steps={steps}
          requestedStepId={adultRequestedStepId}
          autoStart={isNewAdultNeedingTour}
          onActiveChange={setIsAdultTourActive}
          onComplete={dismissAllBeacons}
          onFinish={(payload) => {
            const reason = payload?.reason;
            const stepId = payload?.stepId;
            if (reason === "completed" || reason === "skipped") {
              setIsAdultOnboardingEnabled(false);
              void persistTourCompletion();
            }
            if (reason === "closed" && stepId) {
              const idx = beaconDots.findIndex((b) => b.id === stepId);
              if (idx >= 0)
                setBeaconCursor(Math.min(idx + 1, beaconDots.length));
            }
            setAdultRequestedStepId(null);
          }}
        />
      )}

      {!isAdultTourActive && visibleBeacon && (
        <Beacon
          key={visibleBeacon.id}
          beaconId={visibleBeacon.id}
          selector={visibleBeacon.selector}
          userKey={userKey}
          storageKey={beaconsStorageKey}
          enabled={visibleBeacon.enabled}
          onOpen={(id) => {
            const step = steps.find((s) => s.id === id);
            if (step?.ensurePath && location.pathname !== step.ensurePath) {
              navigate(step.ensurePath);
            }
            setAdultRequestedStepId(id);
          }}
        />
      )}
    </>
  );
}
